/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * MenuEditor — full-featured Menu / Navigation configuration panel.
 * 10 collapsible accordion sections covering every aspect of a nav menu.
 */

import React, { useState, useRef, useCallback } from 'react';
import { useTheme } from '../../../theme/ThemeContext';
import { usePages } from '../../../hooks/usePages';

// ─── uid helper ────────────────────────────────────────────────────────────────
const uid = () => `item_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

// ─── Default item template ────────────────────────────────────────────────────
export const DEFAULT_ITEM = {
  itemId: '',
  label: 'Menu Item',
  labelVisible: true,
  icon: '',
  iconPosition: 'left',
  iconVisible: false,
  tooltip: '',
  navType: 'section',
  targetSectionId: '',
  targetSectionLabel: '',
  scrollBehavior: 'smooth',
  scrollOffset: 80,
  highlightWhenActive: true,
  targetPageId: '',
  targetPageName: '',
  targetPageType: 'public',
  targetPageSlug: '',
  openInNewTab: false,
  passQueryParams: false,
  queryParams: [],
  requireAuth: false,
  url: '',
  isExternal: false,
  disabled: false,
  badge: '',
  badgeColor: '#ef4444',
  isActive: false,
  children: [],
  dropdownStyle: 'dropdown',
  megaMenuColumns: 2,
  megaMenuDescription: '',
  megaMenuFeaturedImage: '',
  labelColor: '',
  fontFamily: '',
  fontSize: 0,
  fontWeight: 0,
  letterSpacing: 0,
  textTransform: '',
};

// ─── Default config ───────────────────────────────────────────────────────────
export const DEFAULT_MENU_CONFIG = {
  menuId: '',
  menuName: 'Main Navigation',
  menuRole: 'primary',

  items: [
    { ...DEFAULT_ITEM, itemId: 'default_1', label: 'Features',  navType: 'section' },
    { ...DEFAULT_ITEM, itemId: 'default_2', label: 'About',     navType: 'page' },
    { ...DEFAULT_ITEM, itemId: 'default_3', label: 'Docs',      navType: 'url', url: 'https://docs.example.com', isExternal: true, openInNewTab: true },
  ],

  // Navigation behavior
  scrollSpyEnabled:      true,
  scrollSpyOffset:       80,
  scrollSpyThreshold:    0.3,
  activeClass:           'active',
  hashUpdateOnScroll:    false,
  smoothScrollDuration:  600,
  scrollEasing:          'ease-in-out',

  // Layout
  menuLayout:            'horizontal',
  menuPosition:          'sticky-top',
  alignment:             'right',
  mobileBreakpoint:      768,
  mobileMenuStyle:       'hamburger-drawer',
  mobileDrawerSide:      'right',
  collapseOnItemClick:   true,
  stickyOffset:          0,
  hideOnScroll:          false,
  hideOnScrollThreshold: 100,

  // Logo
  logoVisible:       true,
  logoType:          'text',
  logoImageUrl:      '',
  logoText:          'My App',
  logoFontSize:      20,
  logoFontWeight:    700,
  logoColor:         '',
  logoWidth:         120,
  logoHeight:        40,
  logoLinkNavType:   'url',
  logoLinkTarget:    '/',
  logoUseAppDefault: true,

  // CTA (legacy single-button fields kept for backward compat)
  navCtaVisible:       false,
  navCtaLabel:         'Get Started',
  navCtaNavType:       'url',
  navCtaTarget:        '/sign-up',
  navCtaStyle:         'filled',
  navCtaColor:         '',
  navCtaTextColor:     '#ffffff',
  navCtaBorderRadius:  6,
  navCtaSize:          'md',
  navCtaOpenNewTab:    false,

  // Multi-CTA buttons (up to 3)
  navCtaButtons:       [],

  // Styling
  fontFamily:               '',
  fontSize:                 14,
  fontWeight:               500,
  labelColor:               '#1e293b',
  labelColorHover:          '',
  labelColorActive:         '',
  activeIndicator:          'underline',
  activeIndicatorColor:     '',
  activeIndicatorThickness: 2,
  itemSpacing:              8,
  itemPaddingX:             12,
  itemPaddingY:             8,
  itemBorderRadius:         4,
  itemHoverBgColor:         '',
  itemActiveBgColor:        '',
  navBgColor:               '#ffffff',
  navBgOpacity:             100,
  navBgBlur:                false,
  navBgBlurStrength:        8,
  navBorderBottom:          true,
  navBorderColor:           '#e2e8f0',
  navBorderWidth:           1,
  navHeight:                64,
  navPaddingX:              24,
  navShadow:                'sm',
  navShadowColor:           '',
  scrolledBgColor:          '#ffffff',
  scrolledBgOpacity:        95,
  scrolledTriggerOffset:    50,

  // Dropdown
  dropdownBgColor:          '#ffffff',
  dropdownBorderColor:      '#e2e8f0',
  dropdownBorderRadius:     8,
  dropdownShadow:           'md',
  dropdownItemColor:        '#374151',
  dropdownItemHoverBgColor: '#f1f5f9',
  dropdownItemPaddingX:     16,
  dropdownItemPaddingY:     8,
  dropdownOffset:           4,
  dropdownAnimation:        'fade',
  dropdownTrigger:          'hover',

  // Mobile
  mobileMenuBgColor:        '#1e293b',
  mobileMenuTextColor:      '#f1f5f9',
  mobileMenuOverlayColor:   '#000000',
  mobileMenuOverlayOpacity: 50,
  hamburgerIconColor:       '#1e293b',
  hamburgerIconSize:        24,
  hamburgerAnimationStyle:  'cross',
  mobileMenuAnimation:      'slide',
  mobileMenuWidth:          280,

  // Animation
  entranceAnimation:  'none',
  animationDuration:  400,
  animationDelay:     0,
  transitionDuration: 200,

  // SEO & Accessibility
  ariaLabel:          'Main navigation',
  role:               'navigation',
  id:                 '',
  customClassName:    '',
  skipNavLink:        false,
  skipNavTarget:      '',
  keyboardNavEnabled: true,

  // Custom CSS
  customCss: '',
};

// ─── Style fields for copy/paste ──────────────────────────────────────────────
const STYLE_FIELDS = [
  'fontFamily','fontSize','fontWeight','labelColor','labelColorHover','labelColorActive',
  'activeIndicator','activeIndicatorColor','activeIndicatorThickness',
  'itemSpacing','itemPaddingX','itemPaddingY','itemBorderRadius','itemHoverBgColor','itemActiveBgColor',
  'navBgColor','navBgOpacity','navBgBlur','navBgBlurStrength',
  'navBorderBottom','navBorderColor','navBorderWidth','navHeight','navPaddingX',
  'navShadow','navShadowColor','scrolledBgColor','scrolledBgOpacity','scrolledTriggerOffset',
  'dropdownBgColor','dropdownBorderColor','dropdownBorderRadius','dropdownShadow',
  'dropdownItemColor','dropdownItemHoverBgColor','dropdownItemPaddingX','dropdownItemPaddingY',
  'dropdownOffset','dropdownAnimation','dropdownTrigger',
  'mobileMenuBgColor','mobileMenuTextColor','mobileMenuOverlayColor','mobileMenuOverlayOpacity',
  'hamburgerIconColor','hamburgerIconSize','hamburgerAnimationStyle','mobileMenuAnimation','mobileMenuWidth',
  'entranceAnimation','animationDuration','animationDelay','transitionDuration',
];

// ─── Presets ──────────────────────────────────────────────────────────────────
const mkItems = (defs) => defs.map(([label, navType, extra]) => ({
  ...DEFAULT_ITEM, itemId: uid(), label, navType, ...(extra || {}),
}));

const PRESETS = [
  {
    key: 'landing', label: 'Landing Page Nav', icon: 'bi-rocket-takeoff',
    description: 'Logo left, section-scroll items, CTA, sticky-top, scroll spy',
    config: {
      ...DEFAULT_MENU_CONFIG,
      menuPosition: 'sticky-top', alignment: 'right',
      scrollSpyEnabled: true, navCtaVisible: true, navCtaLabel: 'Get Started', navCtaStyle: 'filled',
      items: mkItems([['Features','section'],['Pricing','section'],['About','section'],['Contact','section']]),
    },
  },
  {
    key: 'multipage', label: 'Multi-page Site Nav', icon: 'bi-grid',
    description: 'Logo left, 5 page-link items, fixed-top',
    config: {
      ...DEFAULT_MENU_CONFIG,
      menuPosition: 'fixed-top', alignment: 'right', scrollSpyEnabled: false, navCtaVisible: false,
      items: mkItems([['Home','page'],['About','page'],['Services','page'],['Blog','page'],['Contact','page']]),
    },
  },
  {
    key: 'saas', label: 'SaaS Product Nav', icon: 'bi-cpu',
    description: 'Frosted glass bg, mega-menu, CTA, sticky',
    config: {
      ...DEFAULT_MENU_CONFIG,
      menuPosition: 'sticky-top', alignment: 'right',
      navBgBlur: true, navBgBlurStrength: 12, navBgOpacity: 85,
      navCtaVisible: true, navCtaLabel: 'Start free trial', navCtaStyle: 'filled',
      scrollSpyEnabled: false,
      items: [
        { ...DEFAULT_ITEM, itemId: uid(), label: 'Features', navType: 'section',
          children: [
            { ...DEFAULT_ITEM, itemId: uid(), label: 'Analytics', navType: 'section' },
            { ...DEFAULT_ITEM, itemId: uid(), label: 'Automation', navType: 'section' },
          ], dropdownStyle: 'mega-menu', megaMenuColumns: 2 },
        ...mkItems([['Pricing','page'],['Docs','url',{url:'https://docs.example.com',isExternal:true,openInNewTab:true}],['Blog','page']]),
      ],
    },
  },
  {
    key: 'hamburger', label: 'Minimal Hamburger', icon: 'bi-list',
    description: 'Mobile-first, fullscreen overlay on all breakpoints',
    config: {
      ...DEFAULT_MENU_CONFIG,
      menuLayout: 'hamburger', menuPosition: 'fixed-top', mobileBreakpoint: 9999,
      mobileMenuStyle: 'fullscreen-overlay', navBorderBottom: false, navCtaVisible: false,
      mobileMenuBgColor: '#0f172a', mobileMenuTextColor: '#f1f5f9',
      hamburgerAnimationStyle: 'cross', mobileMenuAnimation: 'fade',
      items: mkItems([['Home','page'],['About','page'],['Services','page'],['Contact','page']]),
    },
  },
];

// ─── UI Primitives ────────────────────────────────────────────────────────────

function Toggle({ value, onChange, disabled }) {
  return (
    <div
      onClick={() => !disabled && onChange(!value)}
      style={{
        width: 36, height: 20, borderRadius: 10, cursor: disabled ? 'default' : 'pointer',
        background: value ? 'var(--primary-color)' : '#e2e8f0',
        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div style={{
        position: 'absolute', top: 3, left: value ? 18 : 3,
        width: 14, height: 14, borderRadius: '50%', background: '#fff',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

function Seg({ value, onChange, options, small }) {
  return (
    <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
      {options.map(opt => (
        <button key={opt.value} type="button" onClick={() => onChange(opt.value)} style={{
          flex: 1, padding: small ? '3px 0' : '5px 0', border: 'none',
          borderRight: '1px solid #e2e8f0',
          background: value === opt.value ? '#1e293b' : '#fff',
          color: value === opt.value ? '#fff' : '#374151',
          fontSize: small ? 10 : 11, fontWeight: value === opt.value ? 600 : 400,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
        }}>
          {opt.icon && <i className={`bi ${opt.icon}`} style={{ fontSize: small ? 10 : 12 }} />}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function ColorField({ value, onChange, allowEmpty, emptyLabel }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input
        type="color"
        value={value || '#ffffff'}
        onChange={e => onChange(e.target.value)}
        style={{ width: 32, height: 26, padding: 2, border: '1px solid #e2e8f0', borderRadius: 4, cursor: 'pointer' }}
      />
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={allowEmpty ? (emptyLabel || 'Auto') : '#000000'}
        style={{ width: 80, fontSize: 11, padding: '2px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }}
      />
      {allowEmpty && value && (
        <button type="button" onClick={() => onChange('')}
          style={{ fontSize: 10, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          Clear
        </button>
      )}
    </div>
  );
}

function NumInput({ value, onChange, min, max, suffix, step, style: extraStyle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, ...extraStyle }}>
      <input
        type="number" min={min} max={max} step={step || 1}
        value={value ?? 0}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width: 64, fontSize: 12, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4, textAlign: 'right' }}
      />
      {suffix && <span style={{ fontSize: 11, color: '#94a3b8' }}>{suffix}</span>}
    </div>
  );
}

function Row({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</div>
      {hint && <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>{hint}</div>}
      {children}
    </div>
  );
}

function InlineRow({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8, gap: 8 }}>
      <span style={{ fontSize: 11, color: '#374151', flexShrink: 0 }}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>{children}</div>
    </div>
  );
}

function SectionDivider({ label }) {
  return (
    <div style={{ margin: '16px 0 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
    </div>
  );
}

function Accordion({ label, icon, defaultOpen = false, badge, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: '1px solid #f1f5f9' }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 8,
        padding: '10px 0', background: 'none', border: 'none', cursor: 'pointer',
        color: '#1e293b', fontWeight: 600, fontSize: 12,
      }}>
        {icon && <i className={`bi ${icon}`} style={{ fontSize: 13, color: '#64748b' }} />}
        <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
        {badge && <span className="badge" style={{ background: '#f1f5f9', color: '#64748b', fontSize: 9, fontWeight: 600 }}>{badge}</span>}
        <i className={`bi ${open ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ fontSize: 11, color: '#94a3b8' }} />
      </button>
      {open && <div style={{ paddingBottom: 14 }}>{children}</div>}
    </div>
  );
}

// ─── Section Picker ───────────────────────────────────────────────────────────

function SectionPicker({ value, onChange, allSections }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const sections = (allSections || []).filter(s =>
    !search || (s.section_type || '').toLowerCase().includes(search.toLowerCase()) ||
    (s.sectionName || s.section_type || '').toLowerCase().includes(search.toLowerCase())
  );

  const selected = allSections?.find(s => String(s.id || s._clientId) === String(value));
  const label = selected
    ? (selected.sectionName || selected.section_type?.replace(/_/g, ' ') || 'Section')
    : '';

  return (
    <div style={{ position: 'relative' }} ref={ref}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px',
          fontSize: 12, cursor: 'pointer', background: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        {value && label ? (
          <span><i className="bi bi-hash me-1" style={{ color: '#7c3aed' }} />{label}</span>
        ) : (
          <span style={{ color: '#94a3b8' }}>Select a section…</span>
        )}
        <i className="bi bi-chevron-down" style={{ fontSize: 10, color: '#94a3b8' }} />
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 2,
        }}>
          <div style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
            <input
              autoFocus
              type="text"
              placeholder="Search sections…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 4, padding: '4px 8px', fontSize: 12 }}
            />
          </div>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {sections.length === 0 && (
              <div style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 12 }}>
                {(allSections || []).length === 0 ? 'No sections on canvas yet' : 'No sections match'}
              </div>
            )}
            {sections.map((s, i) => {
              // Prefer the DB id (integer, known to the client) over the frontend _clientId.
              // After a save both exist; before a first save only _clientId exists.
              const id = s.id || s._clientId || i;
              const name = s.sectionName || s.section_type?.replace(/_/g, ' ') || 'Section';
              return (
                <div key={id} onClick={() => { onChange(String(id)); setOpen(false); setSearch(''); }}
                  style={{
                    padding: '8px 12px', cursor: 'pointer', fontSize: 12,
                    background: value === String(id) ? '#f5f3ff' : 'transparent',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = value === String(id) ? '#f5f3ff' : 'transparent'}
                >
                  <i className="bi bi-hash" style={{ color: '#7c3aed', fontSize: 11 }} />
                  <span style={{ fontWeight: 500 }}>{name}</span>
                  <span style={{ color: '#94a3b8', marginLeft: 'auto', fontSize: 10 }}>
                    {s.section_type?.replace(/_/g, ' ')}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page Picker ──────────────────────────────────────────────────────────────

function PagePicker({ value, onChange, pages }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const pub  = (pages || []).filter(p => (p.visibility || 'public') === 'public'  && (!search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.slug?.toLowerCase().includes(search.toLowerCase())));
  const priv = (pages || []).filter(p => (p.visibility || 'public') === 'private' && (!search || p.name?.toLowerCase().includes(search.toLowerCase()) || p.slug?.toLowerCase().includes(search.toLowerCase())));

  const selected = (pages || []).find(p => String(p.id) === String(value));

  return (
    <div style={{ position: 'relative' }}>
      <div onClick={() => setOpen(o => !o)} style={{
        border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px',
        fontSize: 12, cursor: 'pointer', background: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        {selected ? (
          <span>
            <i className={`bi ${selected.visibility === 'private' ? 'bi-lock-fill' : 'bi-globe'} me-1`}
              style={{ color: selected.visibility === 'private' ? '#475569' : '#16a34a', fontSize: 10 }} />
            {selected.name}
            <span style={{ color: '#94a3b8', marginLeft: 6, fontSize: 10 }}>/{selected.slug}</span>
          </span>
        ) : (
          <span style={{ color: '#94a3b8' }}>Select a page…</span>
        )}
        <i className="bi bi-chevron-down" style={{ fontSize: 10, color: '#94a3b8' }} />
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', marginTop: 2,
        }}>
          <div style={{ padding: 8, borderBottom: '1px solid #f1f5f9' }}>
            <input autoFocus type="text" placeholder="Search pages…" value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 4, padding: '4px 8px', fontSize: 12 }} />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {pub.length > 0 && (
              <>
                <div style={{ padding: '6px 12px 2px', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Public Pages
                </div>
                {pub.map(p => (
                  <PagePickerRow key={p.id} page={p} selected={String(p.id) === String(value)}
                    onSelect={() => { onChange(String(p.id), p); setOpen(false); setSearch(''); }} />
                ))}
              </>
            )}
            {priv.length > 0 && (
              <>
                <div style={{ padding: '6px 12px 2px', fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Private Pages
                </div>
                {priv.map(p => (
                  <PagePickerRow key={p.id} page={p} selected={String(p.id) === String(value)}
                    onSelect={() => { onChange(String(p.id), p); setOpen(false); setSearch(''); }} />
                ))}
              </>
            )}
            {pub.length === 0 && priv.length === 0 && (
              <div style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 12 }}>
                {(pages || []).length === 0 ? 'No pages in workspace yet' : 'No pages match'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PagePickerRow({ page, selected, onSelect }) {
  const isPrivate = page.visibility === 'private';
  return (
    <div onClick={onSelect} style={{
      padding: '7px 12px', cursor: 'pointer', fontSize: 12,
      background: selected ? '#f5f3ff' : 'transparent',
      display: 'flex', alignItems: 'center', gap: 8,
    }}
      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
      onMouseLeave={e => e.currentTarget.style.background = selected ? '#f5f3ff' : 'transparent'}
    >
      <i className={`bi ${isPrivate ? 'bi-lock-fill' : 'bi-globe'}`}
        style={{ color: isPrivate ? '#475569' : '#16a34a', fontSize: 11 }} />
      <span style={{ fontWeight: 500, flex: 1 }}>{page.name}</span>
      {page.status === 'draft' && (
        <span style={{ fontSize: 9, background: '#fef9c3', color: '#854d0e', borderRadius: 3, padding: '1px 4px', fontWeight: 600 }}>Draft</span>
      )}
      <span style={{ color: '#94a3b8', fontSize: 10 }}>/{page.slug}</span>
    </div>
  );
}

// ─── Query Params Editor ──────────────────────────────────────────────────────

function QueryParamsEditor({ params, onChange }) {
  const add = () => onChange([...params, { key: '', value: '' }]);
  const remove = i => onChange(params.filter((_, idx) => idx !== i));
  const update = (i, field, val) => onChange(params.map((p, idx) => idx === i ? { ...p, [field]: val } : p));

  return (
    <div style={{ marginTop: 6 }}>
      {params.map((p, i) => (
        <div key={i} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
          <input type="text" placeholder="key" value={p.key} onChange={e => update(i, 'key', e.target.value)}
            style={{ flex: 1, fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }} />
          <span style={{ color: '#94a3b8', fontSize: 11 }}>=</span>
          <input type="text" placeholder="value" value={p.value} onChange={e => update(i, 'value', e.target.value)}
            style={{ flex: 1, fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }} />
          <button type="button" onClick={() => remove(i)}
            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '0 2px', fontSize: 13 }}>
            <i className="bi bi-x" />
          </button>
        </div>
      ))}
      <button type="button" onClick={add}
        style={{ fontSize: 11, color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
        <i className="bi bi-plus" /> Add param
      </button>
    </div>
  );
}

// ─── Nav Type Selector ────────────────────────────────────────────────────────

function NavTypeSelector({ value, onChange }) {
  const options = [
    { value: 'section', icon: 'bi-hash',       label: 'Section' },
    { value: 'page',    icon: 'bi-file-earmark', label: 'Page' },
    { value: 'url',     icon: 'bi-link-45deg',  label: 'URL' },
    { value: 'none',    icon: 'bi-dash',         label: 'None' },
  ];
  return (
    <div style={{ display: 'flex', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
      {options.map(opt => (
        <button key={opt.value} type="button" onClick={() => onChange(opt.value)} style={{
          flex: 1, padding: '8px 0', border: 'none',
          borderRight: '1px solid #e2e8f0',
          background: value === opt.value ? 'var(--primary-color)' : '#fff',
          color: value === opt.value ? '#fff' : '#374151',
          fontSize: 10, fontWeight: value === opt.value ? 700 : 400,
          cursor: 'pointer', display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 2,
        }}>
          <i className={`bi ${opt.icon}`} style={{ fontSize: 14 }} />
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ─── Item Editor (inline, 2 tabs) ─────────────────────────────────────────────

function ItemEditor({ item, onUpdate, allSections, workspacePages, depth }) {
  const [tab, setTab] = useState('content');

  const set = (patch) => onUpdate({ ...item, ...patch });

  const sectionNotFound = item.navType === 'section' && item.targetSectionId &&
    !(allSections || []).some(s => String(s.sectionId || s._clientId || s.id) === String(item.targetSectionId));

  const selectedPage = item.navType === 'page' && item.targetPageId
    ? (workspacePages || []).find(p => String(p.id) === String(item.targetPageId))
    : null;

  const handlePageSelect = (pageId, page) => {
    set({
      targetPageId: pageId,
      targetPageName: page?.name || '',
      targetPageSlug: page?.slug || '',
      targetPageType: page?.visibility || 'public',
      requireAuth: page?.visibility === 'private',
    });
  };

  return (
    <div style={{ background: '#f8fafc', borderRadius: 8, padding: 12, marginTop: 4 }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 12, border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
        {[{key:'content',label:'Content & Nav'},{key:'style',label:'Style'}].map(t => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)} style={{
            flex: 1, padding: '5px 0', border: 'none', fontSize: 11, fontWeight: tab === t.key ? 700 : 400,
            background: tab === t.key ? '#1e293b' : '#fff',
            color: tab === t.key ? '#fff' : '#64748b', cursor: 'pointer',
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'content' && (
        <div>
          {/* Label */}
          <Row label="Label">
            <input type="text" className="form-control form-control-sm" value={item.label || ''}
              onChange={e => set({ label: e.target.value.slice(0, 30) })}
              maxLength={30} placeholder="Menu item label" />
            <div style={{ fontSize: 10, color: (item.label || '').length >= 30 ? '#dc2626' : '#94a3b8', marginTop: 3, textAlign: 'right' }}>
              {(item.label || '').length} / 30 characters
            </div>
          </Row>

          <InlineRow label="Show label">
            <Toggle value={item.labelVisible !== false} onChange={v => set({ labelVisible: v })} />
          </InlineRow>

          {/* Tooltip */}
          <Row label="Tooltip (hover)">
            <input type="text" className="form-control form-control-sm" value={item.tooltip || ''}
              onChange={e => set({ tooltip: e.target.value.slice(0, 100) })}
              maxLength={100} placeholder="Optional tooltip text" />
            <div style={{ fontSize: 10, color: (item.tooltip || '').length >= 100 ? '#dc2626' : '#94a3b8', marginTop: 3, textAlign: 'right' }}>
              {(item.tooltip || '').length} / 100 characters
            </div>
          </Row>

          {/* Navigation type */}
          <Row label="Navigation Type">
            <NavTypeSelector value={item.navType || 'section'} onChange={v => set({ navType: v })} />
          </Row>

          {/* Section scroll */}
          {item.navType === 'section' && (
            <div style={{ background: '#eff6ff', borderRadius: 6, padding: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#2563eb', marginBottom: 8 }}>
                <i className="bi bi-hash me-1" /> Section Scroll Settings
              </div>
              {sectionNotFound && (
                <div style={{ fontSize: 10, background: '#fef2f2', color: '#dc2626', borderRadius: 4, padding: '4px 8px', marginBottom: 8 }}>
                  <i className="bi bi-exclamation-triangle me-1" /> Section not found — update link
                </div>
              )}
              <Row label="Target section">
                <SectionPicker value={item.targetSectionId} onChange={v => set({ targetSectionId: v })} allSections={allSections} />
              </Row>
              <InlineRow label="Scroll behavior">
                <Seg value={item.scrollBehavior || 'smooth'} onChange={v => set({ scrollBehavior: v })}
                  options={[{value:'smooth',label:'Smooth'},{value:'instant',label:'Instant'}]} small />
              </InlineRow>
              <InlineRow label="Scroll offset">
                <NumInput value={item.scrollOffset ?? 80} onChange={v => set({ scrollOffset: v })} min={0} max={300} suffix="px" />
              </InlineRow>
              <div style={{ fontSize: 10, color: '#64748b', marginTop: -4, marginBottom: 8 }}>
                Compensates for sticky header height
              </div>
              <InlineRow label="Highlight when in view">
                <Toggle value={item.highlightWhenActive !== false} onChange={v => set({ highlightWhenActive: v })} />
              </InlineRow>
            </div>
          )}

          {/* Page link */}
          {item.navType === 'page' && (
            <div style={{ background: '#f0fdf4', borderRadius: 6, padding: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#16a34a', marginBottom: 8 }}>
                <i className="bi bi-file-earmark me-1" /> Page Link Settings
              </div>
              <Row label="Target page">
                <PagePicker value={item.targetPageId} onChange={handlePageSelect} pages={workspacePages} />
              </Row>
              {selectedPage?.status === 'draft' && (
                <div style={{ fontSize: 10, background: '#fef9c3', color: '#854d0e', borderRadius: 4, padding: '4px 8px', marginBottom: 8 }}>
                  <i className="bi bi-exclamation-triangle me-1" /> Linking to a Draft page may result in a 404
                </div>
              )}
              {selectedPage?.visibility === 'private' && (
                <div style={{ fontSize: 10, background: '#f0f9ff', color: '#0369a1', borderRadius: 4, padding: '4px 8px', marginBottom: 8 }}>
                  <i className="bi bi-lock me-1" /> Visitors will be redirected to login
                </div>
              )}
              {!item.targetPageId && (
                <Row label="Or enter slug manually">
                  <input type="text" className="form-control form-control-sm" value={item.targetPageSlug || ''}
                    onChange={e => set({ targetPageSlug: e.target.value })} placeholder="/about" />
                </Row>
              )}
            </div>
          )}

          {/* External URL */}
          {item.navType === 'url' && (
            <div style={{ background: '#fff7ed', borderRadius: 6, padding: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#ea580c', marginBottom: 8 }}>
                <i className="bi bi-link-45deg me-1" /> External URL Settings
              </div>
              <Row label="URL">
                <input type="url" className="form-control form-control-sm" value={item.url || ''}
                  onChange={e => set({ url: e.target.value, isExternal: e.target.value.startsWith('http') })}
                  placeholder="https://example.com" />
              </Row>
            </div>
          )}

          {item.navType === 'none' && (
            <div style={{ fontSize: 11, color: '#94a3b8', background: '#f8fafc', borderRadius: 6, padding: '8px 10px', marginBottom: 10 }}>
              <i className="bi bi-info-circle me-1" /> No link — acts as a parent label for dropdowns
            </div>
          )}

          {/* Common link behaviour */}
          {(item.navType === 'page' || item.navType === 'url') && (
            <InlineRow label="Open in new tab">
              <Toggle value={item.openInNewTab} onChange={v => set({ openInNewTab: v })} />
            </InlineRow>
          )}

          {/* Badge */}
          <SectionDivider label="Badge" />
          <Row label="Badge text">
            <input type="text" className="form-control form-control-sm" value={item.badge || ''}
              onChange={e => set({ badge: e.target.value })} placeholder='e.g. "New", "Hot"' />
          </Row>
          {item.badge && (
            <Row label="Badge color">
              <ColorField value={item.badgeColor || '#ef4444'} onChange={v => set({ badgeColor: v })} />
            </Row>
          )}
        </div>
      )}

      {tab === 'style' && (
        <div>
          <Row label="Text color">
            <ColorField value={item.labelColor || ''} onChange={v => set({ labelColor: v })} allowEmpty emptyLabel="Global default" />
          </Row>
          <Row label="Font family">
            <select className="form-select form-select-sm" value={item.fontFamily || ''}
              onChange={e => set({ fontFamily: e.target.value })}>
              <option value="">Global default</option>
              <option value="inherit">Inherit</option>
              <option value="'Inter', sans-serif">Inter</option>
              <option value="'Roboto', sans-serif">Roboto</option>
              <option value="'Open Sans', sans-serif">Open Sans</option>
              <option value="'Lato', sans-serif">Lato</option>
              <option value="'Poppins', sans-serif">Poppins</option>
              <option value="'Montserrat', sans-serif">Montserrat</option>
              <option value="'Raleway', sans-serif">Raleway</option>
              <option value="'Playfair Display', serif">Playfair Display</option>
              <option value="'Merriweather', serif">Merriweather</option>
              <option value="Georgia, serif">Georgia</option>
              <option value="'Courier New', monospace">Courier New</option>
            </select>
          </Row>
          <Row label="Font size">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <NumInput value={item.fontSize || 0} onChange={v => set({ fontSize: v })} min={0} max={72} suffix="px" />
              {item.fontSize > 0 && (
                <button type="button" onClick={() => set({ fontSize: 0 })}
                  style={{ fontSize: 10, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                  Reset
                </button>
              )}
              {!item.fontSize && <span style={{ fontSize: 10, color: '#94a3b8' }}>0 = global default</span>}
            </div>
          </Row>
          <Row label="Font weight">
            <Seg value={String(item.fontWeight || 0)} onChange={v => set({ fontWeight: Number(v) })}
              options={[{value:'0',label:'Default'},{value:'400',label:'Regular'},{value:'500',label:'Medium'},{value:'600',label:'SemiBold'},{value:'700',label:'Bold'}]} small />
          </Row>
          <Row label="Letter spacing">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <NumInput value={item.letterSpacing || 0} onChange={v => set({ letterSpacing: v })} min={-5} max={20} step={0.5} suffix="px" />
              {!item.letterSpacing && <span style={{ fontSize: 10, color: '#94a3b8' }}>0 = normal</span>}
            </div>
          </Row>
          <Row label="Text transform">
            <Seg value={item.textTransform || ''}
              onChange={v => set({ textTransform: v })}
              options={[{value:'',label:'None'},{value:'uppercase',label:'AA'},{value:'lowercase',label:'aa'},{value:'capitalize',label:'Aa'}]} small />
          </Row>
        </div>
      )}
    </div>
  );
}

// ─── Nav Type Icon ────────────────────────────────────────────────────────────

function NavTypeIcon({ navType, size }) {
  const map = { section: 'bi-hash', page: 'bi-file-earmark', url: 'bi-link-45deg', none: 'bi-dash' };
  const colors = { section: '#7c3aed', page: '#16a34a', url: '#ea580c', none: '#94a3b8' };
  return (
    <i className={`bi ${map[navType] || 'bi-dash'}`}
      style={{ fontSize: size || 13, color: colors[navType] || '#94a3b8', flexShrink: 0 }} />
  );
}

// ─── Item Card ────────────────────────────────────────────────────────────────

function ItemCard({ item, index, total, onUpdate, onDelete, onDuplicate, allSections, workspacePages,
  onMoveUp, onMoveDown, dragHandleProps }) {
  const [expanded, setExpanded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const sectionNotFound = item.navType === 'section' && item.targetSectionId &&
    !(allSections || []).some(s => String(s.sectionId || s._clientId || s.id) === String(item.targetSectionId));

  return (
    <div
      style={{ marginBottom: 6 }}
      draggable
      onDragStart={dragHandleProps?.onDragStart}
      onDragOver={dragHandleProps?.onDragOver}
      onDrop={dragHandleProps?.onDrop}
      onDragEnd={dragHandleProps?.onDragEnd}
    >
      {/* Card row */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: '#fff', border: '1px solid #e2e8f0',
        borderLeft: `3px solid ${item.navType === 'section' ? '#7c3aed' : item.navType === 'page' ? '#16a34a' : item.navType === 'url' ? '#ea580c' : '#e2e8f0'}`,
        borderRadius: 8, padding: '7px 10px',
        cursor: 'grab',
      }}>
        {/* Drag handle */}
        <i className="bi bi-grip-vertical" style={{ fontSize: 14, color: '#cbd5e1', cursor: 'grab', flexShrink: 0 }} />

        {/* Nav type icon */}
        <NavTypeIcon navType={item.navType} />

        {/* Label */}
        <span style={{ fontSize: 12, fontWeight: 600, flex: 1, color: item.disabled ? '#94a3b8' : '#1e293b' }}>
          {item.label || 'Untitled'}
        </span>

        {/* Badges */}
        {item.navType === 'page' && item.targetPageType === 'private' && (
          <i className="bi bi-lock-fill" title="Private page" style={{ fontSize: 10, color: '#475569' }} />
        )}
        {sectionNotFound && (
          <span title="Section not found — update link"
            style={{ fontSize: 9, background: '#fef2f2', color: '#dc2626', borderRadius: 3, padding: '1px 4px', fontWeight: 600 }}>
            !
          </span>
        )}
        {item.badge && (
          <span style={{ fontSize: 9, background: item.badgeColor || '#ef4444', color: '#fff', borderRadius: 10, padding: '1px 5px', fontWeight: 700 }}>
            {item.badge}
          </span>
        )}
        {/* Up/Down */}
        <button type="button" onClick={onMoveUp} disabled={index === 0}
          style={{ background: 'none', border: 'none', cursor: index === 0 ? 'default' : 'pointer', color: index === 0 ? '#e2e8f0' : '#94a3b8', padding: '0 2px', fontSize: 11 }}>
          <i className="bi bi-chevron-up" />
        </button>
        <button type="button" onClick={onMoveDown} disabled={index === total - 1}
          style={{ background: 'none', border: 'none', cursor: index === total - 1 ? 'default' : 'pointer', color: index === total - 1 ? '#e2e8f0' : '#94a3b8', padding: '0 2px', fontSize: 11 }}>
          <i className="bi bi-chevron-down" />
        </button>

        {/* Edit toggle */}
        <button type="button" onClick={() => setExpanded(o => !o)}
          style={{ background: expanded ? '#f5f3ff' : 'none', border: 'none', cursor: 'pointer', color: expanded ? '#7c3aed' : '#64748b', borderRadius: 4, padding: '2px 6px', fontSize: 11 }}>
          <i className={`bi ${expanded ? 'bi-chevron-up' : 'bi-pencil'}`} />
        </button>

        {/* Three-dot menu */}
        <div style={{ position: 'relative' }}>
          <button type="button" onClick={() => setMenuOpen(o => !o)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '2px 4px', fontSize: 14 }}>
            <i className="bi bi-three-dots-vertical" />
          </button>
          {menuOpen && (
            <div style={{
              position: 'absolute', right: 0, top: '100%', zIndex: 50,
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
              boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: '4px 0', minWidth: 140,
            }}>
              <button type="button" onClick={() => { onDuplicate(); setMenuOpen(false); }}
                style={{ width: '100%', textAlign: 'left', padding: '7px 14px', fontSize: 12, border: 'none', background: 'none', cursor: 'pointer' }}>
                <i className="bi bi-copy me-2" /> Duplicate
              </button>
              <button type="button" onClick={() => { onDelete(); setMenuOpen(false); }}
                style={{ width: '100%', textAlign: 'left', padding: '7px 14px', fontSize: 12, border: 'none', background: 'none', cursor: 'pointer', color: '#dc2626' }}>
                <i className="bi bi-trash me-2" /> Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Expanded editor */}
      {expanded && (
        <div style={{ marginTop: 2 }}>
          <ItemEditor item={item} onUpdate={onUpdate} allSections={allSections} workspacePages={workspacePages} depth={0} />
        </div>
      )}
    </div>
  );
}

// ─── Menu Items Section ───────────────────────────────────────────────────────

function MenuItemsSection({ items, onChange, allSections, workspacePages }) {
  const [addingItem, setAddingItem] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newNavType, setNewNavType] = useState('section');
  const dragIdx = useRef(null);

  const addItem = () => {
    if (!newLabel.trim()) return;
    onChange([...items, { ...DEFAULT_ITEM, itemId: uid(), label: newLabel.trim(), navType: newNavType }]);
    setNewLabel(''); setAddingItem(false); setNewNavType('section');
  };

  const removeItem = (id) => onChange(items.filter(it => it.itemId !== id));

  const duplicateItem = (item) => {
    const dup = { ...item, itemId: uid(), label: `${item.label} (copy)`,
      children: (item.children || []).map(c => ({ ...c, itemId: uid() })) };
    onChange([...items, dup]);
  };

  const updateItem = (id, patch) => onChange(items.map(it => it.itemId === id ? { ...it, ...patch } : it));

  const moveUp = (i) => {
    if (i === 0) return;
    const list = [...items];
    [list[i - 1], list[i]] = [list[i], list[i - 1]];
    onChange(list);
  };

  const moveDown = (i) => {
    if (i === items.length - 1) return;
    const list = [...items];
    [list[i], list[i + 1]] = [list[i + 1], list[i]];
    onChange(list);
  };

  const handleDragStart = (i) => { dragIdx.current = i; };
  const handleDrop = (i) => {
    if (dragIdx.current === null || dragIdx.current === i) return;
    const list = [...items];
    const [moved] = list.splice(dragIdx.current, 1);
    const target = i > dragIdx.current ? i - 1 : i;
    list.splice(target, 0, moved);
    onChange(list);
    dragIdx.current = null;
  };

  return (
    <div>
      {items.length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 12 }}>
          <i className="bi bi-list-ul" style={{ fontSize: 24, display: 'block', marginBottom: 6 }} />
          No items yet — add your first menu item below
        </div>
      )}

      {items.map((item, i) => (
        <ItemCard
          key={item.itemId}
          item={item}
          index={i}
          total={items.length}
          onUpdate={(updated) => updateItem(item.itemId, updated)}
          onDelete={() => removeItem(item.itemId)}
          onDuplicate={() => duplicateItem(item)}
          onMoveUp={() => moveUp(i)}
          onMoveDown={() => moveDown(i)}
          allSections={allSections}
          workspacePages={workspacePages}
          dragHandleProps={{
            onDragStart: () => handleDragStart(i),
            onDragOver: (e) => e.preventDefault(),
            onDrop: () => handleDrop(i),
            onDragEnd: () => { dragIdx.current = null; },
          }}
        />
      ))}

      {/* Add item */}
      {addingItem ? (
        <div style={{
          display: 'flex', gap: 4, marginTop: 8, alignItems: 'center',
          padding: '8px', background: '#f5f3ff', borderRadius: 8, border: '1px solid #e9d5ff',
        }}>
          <input type="text" placeholder="Item label" value={newLabel} onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addItem()}
            autoFocus
            style={{ flex: 1, fontSize: 12, padding: '5px 8px', border: '1px solid #e2e8f0', borderRadius: 4 }} />
          <select value={newNavType} onChange={e => setNewNavType(e.target.value)}
            style={{ fontSize: 11, padding: '5px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }}>
            <option value="section">Section scroll</option>
            <option value="page">Page link</option>
            <option value="url">External URL</option>
            <option value="none">No link</option>
          </select>
          <button type="button" onClick={addItem}
            style={{ background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: 4, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>
            Add
          </button>
          <button type="button" onClick={() => { setAddingItem(false); setNewLabel(''); }}
            style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 15 }}>
            ×
          </button>
        </div>
      ) : (
        <button type="button" onClick={() => setAddingItem(true)}
          style={{
            width: '100%', marginTop: 8, padding: '8px', border: '1.5px dashed #e2e8f0',
            borderRadius: 8, background: 'none', cursor: 'pointer', fontSize: 12,
            color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          }}>
          <i className="bi bi-plus-circle" /> Add menu item
        </button>
      )}
    </div>
  );
}

// ─── CTA Buttons Editor ───────────────────────────────────────────────────────

const DEFAULT_CTA_BUTTON = {
  visible: true,
  label: 'Get Started',
  navType: 'url',
  target: '/sign-up',
  style: 'filled',
  size: 'md',
  color: '',
  textColor: '#ffffff',
  borderRadius: 6,
  openInNewTab: false,
};

function CtaButtonCard({ btn, index, total, onChange, onDelete, onMoveUp, onMoveDown, allSections, workspacePages }) {
  const [expanded, setExpanded] = useState(false);
  const set = (patch) => onChange({ ...btn, ...patch });

  return (
    <div style={{ marginBottom: 6 }}>
      {/* Card header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        background: '#fff', border: '1px solid #e2e8f0',
        borderLeft: `3px solid ${btn.style === 'outlined' ? '#7c3aed' : btn.style === 'ghost' ? '#64748b' : 'var(--primary-color)'}`,
        borderRadius: 8, padding: '7px 10px',
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, flex: 1, color: btn.visible ? '#1e293b' : '#94a3b8' }}>
          {btn.label || 'Untitled'}
        </span>
        <span style={{ fontSize: 9, background: '#f1f5f9', color: '#64748b', borderRadius: 3, padding: '1px 5px', fontWeight: 600 }}>
          {btn.style}
        </span>
        <Toggle value={btn.visible} onChange={v => set({ visible: v })} />
        <button type="button" onClick={onMoveUp} disabled={index === 0}
          style={{ background: 'none', border: 'none', cursor: index === 0 ? 'default' : 'pointer', color: index === 0 ? '#e2e8f0' : '#94a3b8', padding: '0 2px', fontSize: 11 }}>
          <i className="bi bi-chevron-up" />
        </button>
        <button type="button" onClick={onMoveDown} disabled={index === total - 1}
          style={{ background: 'none', border: 'none', cursor: index === total - 1 ? 'default' : 'pointer', color: index === total - 1 ? '#e2e8f0' : '#94a3b8', padding: '0 2px', fontSize: 11 }}>
          <i className="bi bi-chevron-down" />
        </button>
        <button type="button" onClick={() => setExpanded(o => !o)}
          style={{ background: expanded ? '#f5f3ff' : 'none', border: 'none', cursor: 'pointer', color: expanded ? '#7c3aed' : '#64748b', borderRadius: 4, padding: '2px 6px', fontSize: 11 }}>
          <i className={`bi ${expanded ? 'bi-chevron-up' : 'bi-pencil'}`} />
        </button>
        <button type="button" onClick={onDelete}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 13, padding: '0 2px' }}>
          <i className="bi bi-trash" />
        </button>
      </div>

      {/* Expanded config */}
      {expanded && (
        <div style={{ background: '#f8fafc', borderRadius: '0 0 8px 8px', padding: '10px 12px', border: '1px solid #e2e8f0', borderTop: 'none' }}>
          <Row label="Label">
            <input type="text" className="form-control form-control-sm" value={btn.label || ''}
              onChange={e => set({ label: e.target.value })} placeholder="Button label" maxLength={40} />
          </Row>
          <Row label="Link type">
            <Seg value={btn.navType} onChange={v => set({ navType: v })}
              options={[{value:'section',label:'Section'},{value:'page',label:'Page'},{value:'url',label:'URL'}]} small />
          </Row>
          {btn.navType === 'section' && (
            <Row label="Target section">
              <SectionPicker value={btn.target} onChange={v => set({ target: v })} allSections={allSections} />
            </Row>
          )}
          {btn.navType === 'page' && (
            <Row label="Target page">
              <PagePicker value={btn.target} onChange={(id) => set({ target: id })} pages={workspacePages} />
            </Row>
          )}
          {btn.navType === 'url' && (
            <Row label="URL">
              <input type="url" className="form-control form-control-sm" value={btn.target || ''}
                onChange={e => set({ target: e.target.value })} placeholder="/sign-up" />
            </Row>
          )}
          <Row label="Button style">
            <Seg value={btn.style} onChange={v => set({ style: v })}
              options={[{value:'filled',label:'Filled'},{value:'outlined',label:'Outlined'},{value:'ghost',label:'Ghost'}]} />
          </Row>
          <Row label="Size">
            <Seg value={btn.size} onChange={v => set({ size: v })}
              options={[{value:'sm',label:'Small'},{value:'md',label:'Medium'},{value:'lg',label:'Large'}]} small />
          </Row>
          <Row label="Background color">
            <ColorField value={btn.color || ''} onChange={v => set({ color: v })} allowEmpty emptyLabel="Theme" />
          </Row>
          <Row label="Text color">
            <ColorField value={btn.textColor || '#ffffff'} onChange={v => set({ textColor: v })} />
          </Row>
          <InlineRow label="Border radius">
            <NumInput value={btn.borderRadius ?? 6} onChange={v => set({ borderRadius: v })} min={0} max={50} suffix="px" />
          </InlineRow>
          <InlineRow label="Open in new tab">
            <Toggle value={btn.openInNewTab} onChange={v => set({ openInNewTab: v })} />
          </InlineRow>
        </div>
      )}
    </div>
  );
}

// ─── Shared panel (used by MenuEditor section mode AND HeaderFooterEditor) ─────

export function MenuNavPanel({ cfg, set, setItems, allSections, workspacePages, theme }) {
  const items = cfg.items || [];

  const [showPresets, setShowPresets] = useState(false);
  const [navPanelTab, setNavPanelTab] = useState('items');

  const applyPreset = (preset) => {
    set(preset.config);
    setShowPresets(false);
  };

  const copyStyles = () => {
    const styleOnly = {};
    STYLE_FIELDS.forEach(f => { if (cfg[f] !== undefined) styleOnly[f] = cfg[f]; });
    navigator.clipboard.writeText(JSON.stringify(styleOnly, null, 2)).catch(() => {});
  };

  const pasteStyles = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const parsed = JSON.parse(text);
      const styleOnly = {};
      STYLE_FIELDS.forEach(f => { if (parsed[f] !== undefined) styleOnly[f] = parsed[f]; });
      set(styleOnly);
    } catch (_) {}
  };

  const resetToDefault = () => set(DEFAULT_MENU_CONFIG);

  return (
    <div>
      {/* Top action bar */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative' }}>
          <button type="button" onClick={() => setShowPresets(o => !o)}
            style={{ fontSize: 11, padding: '5px 10px', background: '#f5f3ff', color: '#7c3aed', border: '1px solid #e9d5ff', borderRadius: 6, cursor: 'pointer' }}>
            <i className="bi bi-stars me-1" /> Presets
          </button>
          {showPresets && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, zIndex: 60, marginTop: 4,
              background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
              boxShadow: '0 8px 32px rgba(0,0,0,0.14)', padding: '8px 0', width: 230,
            }}>
              {PRESETS.map(p => (
                <button key={p.key} type="button" onClick={() => applyPreset(p)} style={{
                  width: '100%', textAlign: 'left', padding: '9px 14px', border: 'none',
                  background: 'none', cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start',
                }}>
                  <i className={`bi ${p.icon}`} style={{ fontSize: 16, color: theme.primary, flexShrink: 0, marginTop: 1 }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{p.label}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>{p.description}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <button type="button" onClick={resetToDefault}
          style={{ fontSize: 11, padding: '5px 10px', background: '#fff5f5', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer' }}>
          <i className="bi bi-arrow-counterclockwise me-1" /> Reset
        </button>
      </div>

      {/* ── Tab bar ── */}
      <div style={{
        display: 'flex',
        overflowX: 'auto',
        borderBottom: '2px solid #e2e8f0',
        background: '#f8fafc',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        marginLeft: -20,
        marginRight: -20,
        paddingLeft: 20,
      }}>
        {[
          { key: 'items',    label: 'Menu Items', icon: 'bi-list-ul',              badge: items.length > 0 ? `${items.length}` : null },
          { key: 'layout',   label: 'Layout',     icon: 'bi-layout-three-columns', badge: null },
          { key: 'branding', label: 'Branding',   icon: 'bi-at',                   badge: null },
          { key: 'cta',      label: 'CTA',        icon: 'bi-cursor',               badge: (() => { const n = (cfg.navCtaButtons || []).filter(b => b.visible).length; return n ? `${n}` : null; })() },
          { key: 'styles',   label: 'Styles',     icon: 'bi-palette',              badge: null },
        ].map(tab => (
          <button key={tab.key} type="button" onClick={() => setNavPanelTab(tab.key)} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '9px 14px',
            border: 'none',
            borderBottom: navPanelTab === tab.key ? `2px solid ${theme.primary}` : '2px solid transparent',
            marginBottom: -2,
            background: 'transparent',
            color: navPanelTab === tab.key ? theme.primary : '#64748b',
            fontWeight: navPanelTab === tab.key ? 700 : 400,
            fontSize: 11,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            transition: 'color 0.15s',
          }}>
            <i className={`bi ${tab.icon}`} style={{ fontSize: 12 }} />
            {tab.label}
            {tab.badge && (
              <span style={{
                fontSize: 9, fontWeight: 700,
                background: navPanelTab === tab.key ? theme.primary : '#e2e8f0',
                color: navPanelTab === tab.key ? '#fff' : '#64748b',
                borderRadius: 10, padding: '1px 5px', marginLeft: 3,
              }}>
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab content ── */}
      <div style={{ paddingTop: 14 }}>

        {/* ── Menu Items tab ── */}
        {navPanelTab === 'items' && (
          <MenuItemsSection items={items} onChange={setItems} allSections={allSections} workspacePages={workspacePages} />
        )}

        {/* ── Layout & Position tab ── */}
        {navPanelTab === 'layout' && (
          <div>
            <Row label="Menu layout">
              <Seg value={cfg.menuLayout} onChange={v => set({ menuLayout: v })}
                options={[{value:'horizontal',label:'Horizontal'},{value:'hamburger',label:'Hamburger'},{value:'tabs',label:'Tabs'},{value:'pills',label:'Pills'}]} small />
            </Row>
            <Row label="Position">
              <select className="form-select form-select-sm" value={cfg.menuPosition} onChange={e => set({ menuPosition: e.target.value })}>
                <option value="fixed-top">Fixed Top</option>
                <option value="sticky-top">Sticky Top</option>
                <option value="static">Static</option>
              </select>
            </Row>
            <Row label="Alignment">
              <Seg value={cfg.alignment} onChange={v => set({ alignment: v })}
                options={[{value:'left',icon:'bi-align-start',label:'Left'},{value:'center',icon:'bi-align-center',label:'Center'},{value:'right',icon:'bi-align-end',label:'Right'},{value:'space-between',label:'Spread'}]} small />
            </Row>
            {cfg.menuPosition === 'sticky-top' && (
              <InlineRow label="Sticky offset">
                <NumInput value={cfg.stickyOffset} onChange={v => set({ stickyOffset: v })} min={0} max={200} suffix="px" />
              </InlineRow>
            )}
            <SectionDivider label="Mobile" />
            <InlineRow label="Mobile breakpoint">
              <NumInput value={cfg.mobileBreakpoint} onChange={v => set({ mobileBreakpoint: v })} min={320} max={1200} suffix="px" />
            </InlineRow>
            <Row label="Mobile menu style">
              <select className="form-select form-select-sm" value={cfg.mobileMenuStyle} onChange={e => set({ mobileMenuStyle: e.target.value })}>
                <option value="hamburger-drawer">Hamburger Drawer</option>
                <option value="hamburger-dropdown">Hamburger Dropdown</option>
              </select>
            </Row>
            <InlineRow label="Close on item click">
              <Toggle value={cfg.collapseOnItemClick} onChange={v => set({ collapseOnItemClick: v })} />
            </InlineRow>
          </div>
        )}

        {/* ── Branding / Logo & App Name tab ── */}
        {navPanelTab === 'branding' && (
          <div>
            <InlineRow label="Show logo / app name">
              <Toggle value={cfg.logoVisible} onChange={v => set({ logoVisible: v })} />
            </InlineRow>
            {cfg.logoVisible && (
              <>
                <InlineRow label="Use application defaults">
                  <Toggle value={cfg.logoUseAppDefault !== false} onChange={v => set({ logoUseAppDefault: v })} />
                </InlineRow>
                {cfg.logoUseAppDefault !== false ? (
                  <div style={{ fontSize: 11, color: '#166534', background: '#f0fdf4', borderRadius: 6, padding: '8px 10px', border: '1px solid #bbf7d0', marginBottom: 8 }}>
                    <i className="bi bi-check-circle-fill me-1" />
                    Displays the logo and name from Application Settings automatically.
                  </div>
                ) : (
                  <>
                    <Row label="Logo type">
                      <Seg value={cfg.logoType} onChange={v => set({ logoType: v })}
                        options={[{value:'text',label:'Text'},{value:'image',label:'Image'},{value:'both',label:'Both'}]} />
                    </Row>
                    {(cfg.logoType === 'image' || cfg.logoType === 'both') && (
                      <>
                        <Row label="Custom image URL">
                          <input type="url" className="form-control form-control-sm" value={cfg.logoImageUrl || ''}
                            onChange={e => set({ logoImageUrl: e.target.value })} placeholder="https://..." />
                        </Row>
                        <InlineRow label="Width">
                          <NumInput value={cfg.logoWidth} onChange={v => set({ logoWidth: v })} min={24} max={400} suffix="px" />
                        </InlineRow>
                        <InlineRow label="Height">
                          <NumInput value={cfg.logoHeight} onChange={v => set({ logoHeight: v })} min={16} max={200} suffix="px" />
                        </InlineRow>
                      </>
                    )}
                    {(cfg.logoType === 'text' || cfg.logoType === 'both') && (
                      <>
                        <Row label="Display name">
                          <input type="text" className="form-control form-control-sm" value={cfg.logoText || ''}
                            onChange={e => set({ logoText: e.target.value })} placeholder="My App" />
                        </Row>
                        <Row label="Text color">
                          <ColorField value={cfg.logoColor || ''} onChange={v => set({ logoColor: v })} allowEmpty emptyLabel="Theme" />
                        </Row>
                        <InlineRow label="Font size">
                          <NumInput value={cfg.logoFontSize} onChange={v => set({ logoFontSize: v })} min={12} max={48} suffix="px" />
                        </InlineRow>
                        <Row label="Font weight">
                          <Seg value={String(cfg.logoFontWeight || 700)} onChange={v => set({ logoFontWeight: Number(v) })}
                            options={[{value:'400',label:'Regular'},{value:'600',label:'SemiBold'},{value:'700',label:'Bold'}]} small />
                        </Row>
                      </>
                    )}
                    <SectionDivider label="Logo link" />
                    <Row label="Link type">
                      <Seg value={cfg.logoLinkNavType} onChange={v => set({ logoLinkNavType: v })}
                        options={[{value:'section',label:'Section'},{value:'page',label:'Page'},{value:'url',label:'URL'}]} small />
                    </Row>
                    {cfg.logoLinkNavType === 'section' && (
                      <Row label="Target section">
                        <SectionPicker value={cfg.logoLinkTarget} onChange={v => set({ logoLinkTarget: v })} allSections={allSections} />
                      </Row>
                    )}
                    {cfg.logoLinkNavType === 'page' && (
                      <Row label="Target page">
                        <PagePicker value={cfg.logoLinkTarget} onChange={(id) => set({ logoLinkTarget: id })} pages={workspacePages} />
                      </Row>
                    )}
                    {cfg.logoLinkNavType === 'url' && (
                      <Row label="URL">
                        <input type="url" className="form-control form-control-sm" value={cfg.logoLinkTarget || ''}
                          onChange={e => set({ logoLinkTarget: e.target.value })} placeholder="/" />
                      </Row>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* ── CTA Buttons tab ── */}
        {navPanelTab === 'cta' && (
          <div>
            {(cfg.navCtaButtons || []).length === 0 && (
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10, textAlign: 'center', padding: '8px 0' }}>
                No CTA buttons yet. Add up to 3.
              </div>
            )}
            {(cfg.navCtaButtons || []).map((btn, i) => (
              <CtaButtonCard
                key={btn.id}
                btn={btn}
                index={i}
                total={(cfg.navCtaButtons || []).length}
                allSections={allSections}
                workspacePages={workspacePages}
                onChange={(updated) => {
                  const next = [...(cfg.navCtaButtons || [])];
                  next[i] = updated;
                  set({ navCtaButtons: next });
                }}
                onDelete={() => {
                  const next = (cfg.navCtaButtons || []).filter((_, idx) => idx !== i);
                  set({ navCtaButtons: next });
                }}
                onMoveUp={() => {
                  if (i === 0) return;
                  const next = [...(cfg.navCtaButtons || [])];
                  [next[i - 1], next[i]] = [next[i], next[i - 1]];
                  set({ navCtaButtons: next });
                }}
                onMoveDown={() => {
                  const arr = cfg.navCtaButtons || [];
                  if (i === arr.length - 1) return;
                  const next = [...arr];
                  [next[i], next[i + 1]] = [next[i + 1], next[i]];
                  set({ navCtaButtons: next });
                }}
              />
            ))}
            {(cfg.navCtaButtons || []).length < 3 && (
              <button type="button"
                onClick={() => set({ navCtaButtons: [...(cfg.navCtaButtons || []), { ...DEFAULT_CTA_BUTTON, id: uid() }] })}
                style={{ width: '100%', marginTop: 4, padding: '6px 0', fontSize: 11, color: '#7c3aed', background: '#f5f3ff', border: '1px dashed #c4b5fd', borderRadius: 6, cursor: 'pointer', fontWeight: 500 }}>
                <i className="bi bi-plus me-1" /> Add CTA Button {(cfg.navCtaButtons || []).length > 0 ? `(${(cfg.navCtaButtons || []).length}/3)` : ''}
              </button>
            )}
          </div>
        )}

        {/* ── Styles tab ── */}
        {navPanelTab === 'styles' && (
          <div>
            <SectionDivider label="ACTIVE INDICATOR" />
            <Row label="Indicator style">
              <select className="form-select form-select-sm" value={cfg.activeIndicator} onChange={e => set({ activeIndicator: e.target.value })}>
                <option value="underline">Underline</option>
                <option value="dot">Dot</option>
                <option value="background">Background</option>
                <option value="none">None</option>
              </select>
            </Row>
            {cfg.activeIndicator !== 'none' && (
              <>
                <Row label="Indicator color">
                  <ColorField value={cfg.activeIndicatorColor || ''} onChange={v => set({ activeIndicatorColor: v })} allowEmpty emptyLabel="Theme" />
                </Row>
                <InlineRow label="Thickness">
                  <NumInput value={cfg.activeIndicatorThickness} onChange={v => set({ activeIndicatorThickness: v })} min={1} max={8} suffix="px" />
                </InlineRow>
              </>
            )}

            <SectionDivider label="ITEM SPACING" />
            {(() => {
              const SPACING_PRESETS = [
                { key: 'compact',     label: 'Compact',     desc: 'Tight',      gap: 2,  px: 8,  py: 4,  r: 3  },
                { key: 'standard',    label: 'Standard',    desc: 'Default',    gap: 8,  px: 12, py: 8,  r: 4  },
                { key: 'comfortable', label: 'Comfortable', desc: 'Relaxed',    gap: 12, px: 16, py: 10, r: 6  },
                { key: 'spacious',    label: 'Spacious',    desc: 'Open',       gap: 16, px: 20, py: 12, r: 6  },
                { key: 'rounded',     label: 'Rounded',     desc: 'Pill style', gap: 8,  px: 16, py: 8,  r: 20 },
              ];
              const active = SPACING_PRESETS.find(p =>
                p.gap === cfg.itemSpacing && p.px === cfg.itemPaddingX &&
                p.py === cfg.itemPaddingY && p.r === cfg.itemBorderRadius
              );
              return (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 12 }}>
                  {SPACING_PRESETS.map(p => {
                    const isActive = active?.key === p.key;
                    return (
                      <button key={p.key} type="button"
                        onClick={() => set({ itemSpacing: p.gap, itemPaddingX: p.px, itemPaddingY: p.py, itemBorderRadius: p.r })}
                        style={{
                          padding: '7px 4px', border: `1.5px solid ${isActive ? 'var(--primary-color)' : '#e2e8f0'}`,
                          borderRadius: 7, background: isActive ? '#f5f3ff' : '#fff',
                          cursor: 'pointer', textAlign: 'center',
                        }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: isActive ? 'var(--primary-color)' : '#1e293b' }}>{p.label}</div>
                        <div style={{ fontSize: 9, color: '#94a3b8', marginTop: 2 }}>{p.desc}</div>
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            <Row label="Hover background">
              <ColorField value={cfg.itemHoverBgColor || ''} onChange={v => set({ itemHoverBgColor: v })} allowEmpty emptyLabel="None" />
            </Row>

            <SectionDivider label="NAV BAR" />
            <Row label="Background color">
              <ColorField value={cfg.navBgColor || '#ffffff'} onChange={v => set({ navBgColor: v })} />
            </Row>
            <InlineRow label="BG opacity">
              <NumInput value={cfg.navBgOpacity} onChange={v => set({ navBgOpacity: v })} min={0} max={100} suffix="%" />
            </InlineRow>
            <InlineRow label="Bottom border">
              <Toggle value={cfg.navBorderBottom} onChange={v => set({ navBorderBottom: v })} />
            </InlineRow>
            {cfg.navBorderBottom && (
              <>
                <Row label="Border color">
                  <ColorField value={cfg.navBorderColor || '#e2e8f0'} onChange={v => set({ navBorderColor: v })} />
                </Row>
                <InlineRow label="Border width">
                  <NumInput value={cfg.navBorderWidth} onChange={v => set({ navBorderWidth: v })} min={1} max={8} suffix="px" />
                </InlineRow>
              </>
            )}
            <InlineRow label="Height">
              <NumInput value={cfg.navHeight} onChange={v => set({ navHeight: v })} min={32} max={120} suffix="px" />
            </InlineRow>
            <InlineRow label="Padding X">
              <NumInput value={cfg.navPaddingX} onChange={v => set({ navPaddingX: v })} min={0} max={120} suffix="px" />
            </InlineRow>
            <Row label="Shadow">
              <Seg value={cfg.navShadow} onChange={v => set({ navShadow: v })}
                options={[{value:'none',label:'None'},{value:'sm',label:'SM'},{value:'md',label:'MD'},{value:'lg',label:'LG'}]} small />
            </Row>

            <SectionDivider label="SCROLLED STATE" />
            <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 8 }}>
              Applies when user has scrolled past the trigger offset
            </div>
            <Row label="Background color">
              <ColorField value={cfg.scrolledBgColor || '#ffffff'} onChange={v => set({ scrolledBgColor: v })} />
            </Row>
            <InlineRow label="BG opacity">
              <NumInput value={cfg.scrolledBgOpacity} onChange={v => set({ scrolledBgOpacity: v })} min={0} max={100} suffix="%" />
            </InlineRow>
          </div>
        )}

      </div>

    </div>
  );
}

// ─── Section-mode wrapper ─────────────────────────────────────────────────────

export default function MenuEditor({ section, dispatch, appId, allSections }) {
  const { theme } = useTheme();
  const cfg = { ...DEFAULT_MENU_CONFIG, ...(section.section_config || {}) };

  const { data: pagesData } = usePages(appId);
  const workspacePages = pagesData?.pages || pagesData || [];

  const set = useCallback((patch) => {
    dispatch({ type: 'UPDATE_SECTION_CONFIG', clientId: section._clientId, patch });
  }, [dispatch, section._clientId]);

  const setItems = useCallback((newItems) => {
    set({ items: newItems });
  }, [set]);

  return (
    <MenuNavPanel
      cfg={cfg}
      set={set}
      setItems={setItems}
      allSections={allSections}
      workspacePages={workspacePages}
      theme={theme}
    />
  );
}
